<template>
  <div class="animal-detail-page">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <span class="spinner-emoji">🔮</span>
        <p>加载中...</p>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-card">
        <span class="error-emoji">😢</span>
        <h2>加载失败</h2>
        <p>{{ error }}</p>
        <button @click="$router.back()" class="btn-primary">返回</button>
      </div>
    </div>

    <!-- 详情展示 -->
    <div v-else-if="animalData" class="detail-container">
      <!-- 顶部导航 -->
      <nav class="top-nav">
        <div class="container">
          <div class="nav-content">
            <button @click="$router.back()" class="nav-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 12H5"></path>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              返回
            </button>
            <div class="nav-title">
              <span class="title-emoji">{{ animalData.emoji }}</span>
              <span>动物详情</span>
            </div>
          </div>
        </div>
      </nav>

      <!-- 主内容区 -->
      <main class="main-content">
        <div class="container">
          <!-- 动物头部信息 -->
          <div class="animal-header">
            <div class="animal-emoji-large">{{ animalData.emoji }}</div>
            <h1 class="animal-name-large">{{ animalData.name }}</h1>
            <div class="animal-tags-header">
              <span 
                v-for="tag in animalData.tags" 
                :key="tag"
                class="tag-header"
                :style="{ backgroundColor: animalData.color + '20', color: animalData.color }"
              >
                {{ tag }}
              </span>
            </div>
          </div>

          <!-- 动物描述 -->
          <div class="section card">
            <h2 class="section-title">性格描述</h2>
            <p class="description-text">{{ animalData.description }}</p>
          </div>

          <!-- 核心特质 -->
          <div class="section card">
            <h2 class="section-title">核心特质</h2>
            <ul class="traits-list">
              <li v-for="(trait, index) in animalData.traits" :key="index" class="trait-item">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="check-icon" :style="{ color: animalData.color }">
                  <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                  <path d="m9 11 3 3L22 4"></path>
                </svg>
                {{ trait }}
              </li>
            </ul>
          </div>

          <!-- 适合职业 -->
          <div class="section card">
            <h2 class="section-title">适合职业</h2>
            <div class="careers-grid">
              <span 
                v-for="career in animalData.careers" 
                :key="career"
                class="career-tag"
              >
                {{ career }}
              </span>
            </div>
          </div>

          <!-- 相性配对 -->
          <div class="section card">
            <h2 class="section-title">相性配对</h2>
            <div class="compatibility-grid">
              <div class="compatibility-item best">
                <div class="compatibility-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                  </svg>
                  <div class="compatibility-label">最佳搭档</div>
                </div>
                <div class="animals">
                  <span v-for="name in animalData.compatibility.best" :key="name" class="animal-name-tag">
                    {{ name }}
                  </span>
                </div>
              </div>
              <div class="compatibility-item good">
                <div class="compatibility-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  <div class="compatibility-label">和谐相处</div>
                </div>
                <div class="animals">
                  <span v-for="name in animalData.compatibility.good" :key="name" class="animal-name-tag">
                    {{ name }}
                  </span>
                </div>
              </div>
              <div class="compatibility-item challenging">
                <div class="compatibility-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <div class="compatibility-label">需要磨合</div>
                </div>
                <div class="animals">
                  <span v-for="name in animalData.compatibility.challenging" :key="name" class="animal-name-tag">
                    {{ name }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- 返回按钮 -->
          <div class="action-section">
            <button @click="$router.back()" class="btn-secondary">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 12H5"></path>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              返回
            </button>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { animalArchetypes } from '@/data/animalArchetypes'

const route = useRoute()

const loading = ref(true)
const error = ref(null)
const animalData = ref(null)

onMounted(() => {
  loadAnimalDetail()
})

const loadAnimalDetail = () => {
  try {
    const animalName = route.params.name || route.query.name
    
    if (!animalName) {
      error.value = '缺少动物名称参数'
      loading.value = false
      return
    }

    // 从动物原型数据中查找
    const animal = animalArchetypes[animalName]
    
    if (!animal) {
      error.value = `找不到名为 "${animalName}" 的动物原型`
      loading.value = false
      return
    }

    animalData.value = animal
    loading.value = false
  } catch (err) {
    console.error('加载动物详情失败:', err)
    error.value = '加载动物详情时发生错误'
    loading.value = false
  }
}
</script>

<style scoped>
.animal-detail-page {
  min-height: 100vh;
  background: linear-gradient(135deg, var(--psychology-calm) 0%, var(--psychology-calm) 50%, var(--psychology-warm) 100%);
}

.loading-container,
.error-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.loading-spinner {
  text-align: center;
}

.spinner-emoji {
  font-size: 4rem;
  display: block;
  margin-bottom: 1rem;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.error-card {
  background: white;
  padding: 3rem;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.error-emoji {
  font-size: 4rem;
  display: block;
  margin-bottom: 1rem;
}

.error-card h2 {
  font-size: 1.5rem;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.error-card p {
  color: #718096;
  margin-bottom: 1.5rem;
}

.top-nav {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 50;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.nav-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
}

.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  color: #4a5568;
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 1rem;
}

.nav-link:hover {
  background: #f7fafc;
}

.nav-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1a202c;
}

.title-emoji {
  font-size: 1.5rem;
}

.main-content {
  padding: 2rem 0 4rem 0;
}

.animal-header {
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem 0;
}

.animal-emoji-large {
  font-size: 8rem;
  line-height: 1;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
}

.animal-name-large {
  font-size: 3rem;
  font-weight: bold;
  color: #1a202c;
  margin: 0 0 1.5rem 0;
}

.animal-tags-header {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.tag-header {
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  font-size: 1rem;
  font-weight: 500;
}

.section {
  margin-bottom: 2rem;
}

.card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #1a202c;
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e2e8f0;
}

.description-text {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #2d3748;
  margin: 0;
}

.traits-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.trait-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  font-size: 1.05rem;
  color: #2d3748;
  border-bottom: 1px solid #f7fafc;
}

.trait-item:last-child {
  border-bottom: none;
}

.check-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.careers-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.career-tag {
  padding: 0.75rem 1.25rem;
  background: #f7fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  color: #4a5568;
  font-weight: 500;
  transition: all 0.2s;
}

.career-tag:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
  transform: translateY(-2px);
}

.compatibility-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.compatibility-item {
  padding: 1.5rem;
  border-radius: 16px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.compatibility-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.compatibility-item.best {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #fbbf24;
}

.compatibility-item.good {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border: 2px solid #60a5fa;
}

.compatibility-item.challenging {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 2px solid #f87171;
}

.compatibility-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.compatibility-header svg {
  width: 24px;
  height: 24px;
}

.compatibility-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a202c;
}

.animals {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.animal-name-tag {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  font-size: 0.95rem;
  color: #2d3748;
  font-weight: 500;
}

.action-section {
  display: flex;
  justify-content: center;
  margin-top: 3rem;
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: var(--psychology-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--psychology-success);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--psychology-primary-30);
}

.btn-secondary {
  background: white;
  color: #4a5568;
  border: 2px solid #e2e8f0;
}

.btn-secondary:hover {
  border-color: #cbd5e0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .animal-emoji-large {
    font-size: 6rem;
  }

  .animal-name-large {
    font-size: 2rem;
  }

  .card {
    padding: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .compatibility-grid {
    grid-template-columns: 1fr;
  }
}
</style>

